// src/services/hubAppState.js
// Client for the auth hub's per-app user-data thin layer.
//
// Copied from the fleet template (apps/the-unspoken-dialogue) with the
// finding-your-voice addition of a shared once-per-login hydrate cache —
// this app has two independent consumers of the hub document (AppContext
// via storageService, and the gamification store), and both must draw from
// a single read.
//
// Why: per-app Firestore stores are invisible to account deletion. The hub
// tree (users/{uid}/apps/profound-philosophy) is deleted by construction
// (auth-hub functions/userCleanup.js), so per-user state lives there.
//
// Rules of the pattern:
//   • Local-first. The hub is a sync target — one read per login (hydrate),
//     never on the hot path of a page. Saves are debounced, coalesced patches.
//   • The callables run on the caller's HUB session (authHubApp), so the
//     server derives identity itself — no uid travels in the payload.
//   • The server stamps updatedAt; clients must not.
//
// Australian English throughout.

import { getFunctions, httpsCallable } from 'firebase/functions';
import { authHubApp, authHubAuth } from './firebase';

const APP_ID = 'profound-philosophy';
const HUB_REGION = 'australia-southeast1';
const SAVE_DEBOUNCE_MS = 800;

let callables = null;
function getCallables() {
  if (!authHubApp) return null;
  if (!callables) {
    const fns = getFunctions(authHubApp, HUB_REGION);
    callables = {
      getAppState: httpsCallable(fns, 'getAppState'),
      saveAppState: httpsCallable(fns, 'saveAppState'),
    };
  }
  return callables;
}

/** Whether hub sync is possible in this build (hub app configured). */
export function hubSyncAvailable() {
  return !!authHubApp;
}

/**
 * Convert Firestore Timestamp-like values to ISO strings so patches are
 * clean JSON.
 */
export function toPlainClient(value) {
  if (value === null || value === undefined) return null;
  if (typeof value.toDate === 'function') return value.toDate().toISOString();
  if (Array.isArray(value)) return value.map(toPlainClient);
  if (typeof value === 'object') {
    const out = {};
    for (const [k, v] of Object.entries(value)) out[k] = toPlainClient(v);
    return out;
  }
  return value;
}

// ── Shared once-per-login hydrate cache ─────────────────────────────────────
// Both storageService.loadAllUserData and the gamification store hydrate
// from this single promise; a new uid (or sign-out) resets it.

let hydratePromise = null;
let hydratedForUid = null;

/**
 * Fetch the full thin-layer doc for this app, once per login. Concurrent and
 * repeat callers share the same read. Returns the doc or null.
 */
export function hydrateAppState() {
  const uid = authHubAuth?.currentUser?.uid || null;
  if (!uid) return Promise.resolve(null);
  if (!hydratePromise || hydratedForUid !== uid) {
    hydratedForUid = uid;
    hydratePromise = fetchAppState();
  }
  return hydratePromise;
}

/** Forget the cached hydrate (sign-out, or clear-my-data). */
export function resetHydrateCache() {
  hydratePromise = null;
  hydratedForUid = null;
}

/** Fetch the full thin-layer doc for this app, or null. */
export async function fetchAppState() {
  const c = getCallables();
  if (!c) return null;
  try {
    const res = await c.getAppState({ appId: APP_ID });
    const payload = res?.data;
    return payload?.exists ? payload.data : null;
  } catch (err) {
    console.error('[hubAppState] load failed:', err);
    return null;
  }
}

// ── Debounced, coalescing patch queue ───────────────────────────────────────

let pendingPatch = null;
let debounceTimer = null;

/** Two-level merge so {moduleProgress:{m1}} + {moduleProgress:{m2}} coalesce. */
function mergePatches(base, extra) {
  const out = { ...base };
  for (const [key, value] of Object.entries(extra)) {
    const bothMaps =
      value && typeof value === 'object' && !Array.isArray(value) &&
      out[key] && typeof out[key] === 'object' && !Array.isArray(out[key]);
    out[key] = bothMaps ? { ...out[key], ...value } : value;
  }
  return out;
}

/** Queue a partial update; multiple queued patches coalesce into one write. */
export function queuePatch(patch) {
  if (!getCallables()) return;
  pendingPatch = mergePatches(pendingPatch || {}, toPlainClient(patch) || {});
  clearTimeout(debounceTimer);
  debounceTimer = setTimeout(() => { flushPatch(); }, SAVE_DEBOUNCE_MS);
}

/** Flush the queue now (login merge, logout, tab hidden). */
export async function flushPatch() {
  clearTimeout(debounceTimer);
  debounceTimer = null;
  if (!pendingPatch) return;
  const patch = pendingPatch;
  pendingPatch = null;
  const c = getCallables();
  if (!c) return;
  try {
    await c.saveAppState({ appId: APP_ID, patch });
  } catch (err) {
    // Keep the failed patch so the next local action retries it; anything
    // queued while the call was in flight wins over the failed snapshot.
    console.error('[hubAppState] save failed (will retry on next change):', err);
    pendingPatch = mergePatches(patch, pendingPatch || {});
  }
}

/** Immediate write, no debounce — used for clear-my-data. */
export async function savePatchNow(patch) {
  const c = getCallables();
  if (!c) return false;
  try {
    await c.saveAppState({ appId: APP_ID, patch: toPlainClient(patch) });
    return true;
  } catch (err) {
    console.error('[hubAppState] immediate save failed:', err);
    return false;
  }
}

// Best-effort flush when the tab goes to the background so a debounced
// patch isn't lost to a close.
if (typeof document !== 'undefined') {
  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState === 'hidden') flushPatch();
  });
}
